'use client';

import { c as _c } from "react/compiler-runtime";
import { Grid, Heading, Legend, TextPassage } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import type { TreatsLoginLocation } from '@libs/analytics';
import { hasAutoshipInCart, useCart } from '@libs/common-next';
import type { BenefitsItem, LinkAction, LoyaltyBenefitsItem, PRIMARY_BUTTON_STYLE, SECONDARY_BUTTON_STYLE } from '@libs/contentful';
import { NEW_TAB, TARGET_BLANK, TARGET_SELF } from '@libs/contentful';
import { NAVIGATION_URLS } from '@libs/core';
import { BenefitItems } from '../BenefitItems';
import { GenericButton } from '../GenericButton';
import type { SignInFormProps } from '../SignInForm';
import { SignInForm } from '../SignInForm';
import type { CTAWithLinkEntry } from './utils';
import { getButtonVariant } from './utils';
import './InterstitialSignIn.scss';
export type InterstitialCta = {
  fields: CTAWithLinkEntry;
};
export type InterstitialLinkAction = {
  fields: LinkAction;
};
type InterstitialSignInProps = SignInFormProps & {
  title?: string;
  subtext?: string;
  benefitItems?: BenefitsItem[] | LoyaltyBenefitsItem[];
  createAccountCta?: InterstitialCta;
  itemsTitle?: string;
  createAccountHref?: string;
  onRegisterClick?: () => void;
  displayAutoshipDescription?: boolean;
  isSoftAuthenticatedServer?: boolean;
  location?: TreatsLoginLocation;
  pathOverride?: string;
  customRedirectUrl?: string | null;
};
const componentClassName = 'interstitial-sign-in';
export function InterstitialSignIn(t0) {
  const $ = _c(42);
  const {
    title,
    subtext,
    benefitItems,
    createAccountCta,
    itemsTitle,
    cta,
    linkAction,
    isInDrawer,
    createAccountHref,
    onRegisterClick,
    onSuccessfulSignIn,
    displayAutoshipDescription,
    isSoftAuthenticatedServer,
    location,
    pathOverride,
    customRedirectUrl
  } = t0;
  const [isCheckoutPage, setIsCheckoutPage] = useState(false);
  const {
    cart
  } = useCart();
  const currentPage = usePathname();
  let t1;
  let t2;
  if ($[0] !== currentPage) {
    t1 = () => {
      if (currentPage) {
        setIsCheckoutPage(["/checkout-login", "/cart"].includes(currentPage));
      }
    };
    t2 = [currentPage];
    $[0] = currentPage;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== cart || $[4] !== displayAutoshipDescription) {
    t3 = displayAutoshipDescription && cart ? hasAutoshipInCart(cart) : false;
    $[3] = cart;
    $[4] = displayAutoshipDescription;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isAutoship = t3;
  let t4;
  if ($[6] !== createAccountCta) {
    t4 = createAccountCta ? getButtonVariant(createAccountCta) : "table-header";
    $[6] = createAccountCta;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const createAccountVariant = t4;
  const createAccountLink = isCheckoutPage ? `${NAVIGATION_URLS.velocity.createAccount}?redirectUrl=/checkout` : createAccountHref ?? createAccountCta?.fields?.link?.fields?.webUrl ?? "/create-account";
  let t5;
  if ($[8] !== cta || $[9] !== customRedirectUrl || $[10] !== isAutoship || $[11] !== isInDrawer || $[12] !== isSoftAuthenticatedServer || $[13] !== linkAction || $[14] !== location || $[15] !== onSuccessfulSignIn || $[16] !== pathOverride) {
    t5 = {
      cta,
      linkAction,
      isAutoship,
      isInDrawer,
      isSoftAuthenticatedServer,
      onSuccessfulSignIn,
      location,
      pathOverride,
      customRedirectUrl
    };
    $[8] = cta;
    $[9] = customRedirectUrl;
    $[10] = isAutoship;
    $[11] = isInDrawer;
    $[12] = isSoftAuthenticatedServer;
    $[13] = linkAction;
    $[14] = location;
    $[15] = onSuccessfulSignIn;
    $[16] = pathOverride;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const signInFormProps = t5;
  let t6;
  if ($[18] !== isAutoship) {
    t6 = isAutoship && <TextPassage capLinelength={false} style={{
      marginBottom: "24px"
    }}>All autoship items require an account to checkout. Please sign in or create an account to continue.</TextPassage>;
    $[18] = isAutoship;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== subtext || $[21] !== title) {
    t7 = (title || subtext) && <Grid.Item className={`${componentClassName}__headings`}>{title && <Heading tagName="h5" className={`${componentClassName}__heading`}>{title}</Heading>}{subtext && <Legend className={`${componentClassName}__sub-heading`} legendSize="sm" required={true} text={subtext} />}</Grid.Item>;
    $[20] = subtext;
    $[21] = title;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== signInFormProps) {
    t8 = <SignInForm key="sign-in-form" {...signInFormProps} />;
    $[23] = signInFormProps;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== benefitItems || $[26] !== isInDrawer || $[27] !== itemsTitle) {
    t9 = true && <div className={isInDrawer ? `${componentClassName}__benefits-container__drawer` : `${componentClassName}__benefits-container`}>{itemsTitle && <Heading size="body-lg-bold" tagName="h2">{itemsTitle}</Heading>}{benefitItems && <BenefitItems data-testid="benefit-items" items={benefitItems} />}</div>;
    $[25] = benefitItems;
    $[26] = isInDrawer;
    $[27] = itemsTitle;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== benefitItems || $[30] !== createAccountCta?.fields?.behavior || $[31] !== createAccountCta?.fields?.text || $[32] !== createAccountLink || $[33] !== createAccountVariant || $[34] !== onRegisterClick) {
    t10 = true && benefitItems && <GenericButton variant={createAccountVariant} text={createAccountCta?.fields?.text || "Create account"} size="lg" target={createAccountCta?.fields?.behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF} href={createAccountLink} onClickCapture={onRegisterClick} rel="noreferrer" data-testid="create-account-btn" fullWidth={true} />;
    $[29] = benefitItems;
    $[30] = createAccountCta?.fields?.behavior;
    $[31] = createAccountCta?.fields?.text;
    $[32] = createAccountLink;
    $[33] = createAccountVariant;
    $[34] = onRegisterClick;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t6 || $[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t11 = <div data-testid="interstitial-sign-in" className={componentClassName}>{t6}{t7}{t8}{t9}{t10}</div>;
    $[36] = t10;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
}